import { useForm } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCustomers } from '@/features/customers/hooks'
import { customerOptionLabel } from '@/features/customers/customer-utils'
import { PriceInput } from '@/components/ui/price-input'
import { useBoats } from '@/features/inventory/hooks'
import type { Consignment } from '@/types/consignment'
import type { Boat } from '@/types/inventory'

export const consignmentFormSchema = z.object({
  boat_id: z.string().min(1, 'Boat is required'),
  consignor_id: z.string().min(1, 'Consignor is required'),
  commission_rate: z.coerce
    .number()
    .gt(0, 'Rate must be greater than 0')
    .max(100, 'Rate cannot exceed 100'),
  asking_price: z.coerce.number().gt(0, 'Asking price must be greater than 0'),
  agreement_end: z.string().optional(),
  notes: z.string().max(5000, 'Notes are at most 5000 characters').optional(),
})

export type ConsignmentFormValues = z.infer<typeof consignmentFormSchema>

const fieldSchemas = consignmentFormSchema.shape

export const boatOptionLabel = (b: Boat) => `${b.year} ${b.make} ${b.model}`.trim()

interface ConsignmentFormProps {
  /** Existing agreement being edited (terms only; boat & consignor are fixed). */
  consignment?: Consignment
  /** Boat preselected when opening the form from a boat's page (create mode). */
  fixedBoat?: Boat
  onSubmit: (values: ConsignmentFormValues) => void
  isPending?: boolean
  submitLabel?: string
}

export function ConsignmentForm({
  consignment,
  fixedBoat,
  onSubmit,
  isPending,
  submitLabel = 'Save',
}: ConsignmentFormProps) {
  const isEdit = !!consignment
  const { data: boats } = useBoats()
  const { data: customers } = useCustomers()

  // The asking price usually starts from the boat's list price.
  const defaultAsking = consignment?.asking_price ?? fixedBoat?.price ?? 0

  const initialValues: ConsignmentFormValues = {
    boat_id: consignment?.boat_id ?? fixedBoat?.id ?? '',
    consignor_id: consignment?.consignor_id ?? '',
    commission_rate: consignment?.commission_rate ?? 10,
    asking_price: defaultAsking,
    agreement_end: consignment?.agreement_end ? consignment.agreement_end.slice(0, 10) : '',
    notes: consignment?.notes ?? '',
  }

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      onSubmit({
        ...value,
        commission_rate: Number(value.commission_rate),
        asking_price: Number(value.asking_price),
      })
    },
  })

  // Only used boats without an open agreement can be consigned (floor plans
  // cover the new ones); the fixed/edit boat stays selectable so reopening a
  // form never clears the selection.
  const selectableBoats = (boats ?? []).filter(
    (b) => b.condition === 'used' && (!b.consignment || b.id === initialValues.boat_id),
  )

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
        className="space-y-4"
      >
        {isEdit || fixedBoat ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {fixedBoat
              ? `Boat: ${boatOptionLabel(fixedBoat)}`
              : `Boat and consignor are fixed once the agreement is signed.`}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              name="boat_id"
              validators={{ onChange: fieldSchemas.boat_id }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Boat</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value as string}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a boat" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {selectableBoats.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {boatOptionLabel(b)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="consignor_id"
              validators={{ onChange: fieldSchemas.consignor_id }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Consignor (owner)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value as string}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a customer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(customers ?? []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {customerOptionLabel(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="commission_rate"
            validators={{ onChange: fieldSchemas.commission_rate }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Commission (%)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.5" min="0" max="100" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="asking_price"
            validators={{ onChange: fieldSchemas.asking_price }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Asking Price ($)</FormLabel>
                <FormControl>
                  <PriceInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="149,000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          name="agreement_end"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Agreement End (optional)</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="notes"
          validators={{ onChange: fieldSchemas.notes }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Agreement terms, reserve price, included gear…"
                  className="h-24 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </form>
    </Form>
  )
}
